import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    Switch,
    Typography
} from '@mui/material';
import {
    ArrowForwardIos,
    Category,
    Edit,
    Image,
    Preview,
    Replay,
    Star,
    WatchLater
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import PageHeaderLayout from '../../../components/Layout/PageHeaderLayout';
import Loading from '../../../components/Loader/Loading';
import { PhoneFrame } from './Section/MobilePreview';
import { SectionServices } from './Section/SectionServices';
import { PermissionGuard } from '../../../components/PermissionGuard';

const componentMeta = {
    bookAgain: {
        icon: <WatchLater sx={{ color: '#ff7043' }} />,
        bg: '#fff0ea',
        title: 'Book Again',
        subtitle: 'Recently booked services'
    },
    categories: {
        icon: <Category sx={{ color: '#2eae6d' }} />,
        bg: '#eaf8f1',
        title: 'Categories',
        subtitle: 'Browse services by category'
    },
    section: {
        icon: <Star sx={{ color: '#fb9800' }} />,
        bg: '#fff4df',
        title: 'Section',
        subtitle: 'Custom section with cards/banners'
    },
    header: {
        icon: <Image sx={{ color: '#1976d2' }} />,
        bg: '#eaf3ff',
        title: 'Header',
        subtitle: 'Top header section with banners or promotions'
    },
    footer: {
        icon: <Image sx={{ color: '#1976d2' }} />,
        bg: '#eaf3ff',
        title: 'Footer',
        subtitle: 'Bottom footer section with quick links'
    }
};

const normalizeTitle = (value) => (value || '').toString().trim().toLowerCase();

const getComponentKind = (section) => {
    const title = normalizeTitle(section?.title);
    if (title === 'book-again') return 'bookAgain';
    if (title === 'category') return 'categories';
    return 'section';
};

const getDisplayTitle = (section) => {
    const kind = getComponentKind(section);
    if (kind === 'bookAgain') return 'Book Again';
    if (kind === 'categories') return 'Categories';
    return section?.title || componentMeta.section.title;
};

const sortByPosition = (a, b) => Number(a?.position || 0) - Number(b?.position || 0);
const getPageType = (section) => section?.displayOptions?.pageType || 'section';
const isWithinSchedule = (section, now = new Date()) => {
    const startDate = section?.startDateTime ? new Date(section.startDateTime) : null;
    const endDate = section?.endDateTime ? new Date(section.endDateTime) : null;

    return section?.isActive !== false
        && (!startDate || startDate <= now)
        && (!endDate || endDate >= now);
};
const selectCurrentFixedSection = (sections) => {
    const sortedSections = [...sections].sort(sortByPosition);
    return sortedSections.find(section => isWithinSchedule(section)) || sortedSections[0] || null;
};

const HomeScreen = () => {
    const menuDispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [header, setHeader] = useState(null);
    const [footer, setFooter] = useState(null);
    const [middleComponents, setMiddleComponents] = useState([]);
    const [previewSections, setPreviewSections] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: 'home-screen'
        });
    }, [menuDispatch]);

    const fetchByType = async (pageType) => {
        const response = await SectionServices.sectionList({
            page: 0,
            limit: 100,
            pageType,
            filters: {
                searchKey: '',
                sortBy: 'position',
                sortType: 'ASC',
                filterOptions: []
            }
        });

        return response.success == '1' ? response.data.list || [] : [];
    };

    const fetchLayout = async () => {
        try {
            setLoading(true);
            const [headers, sections, footers] = await Promise.all([
                fetchByType('header'),
                fetchByType('section'),
                fetchByType('footer')
            ]);

            setHeader(selectCurrentFixedSection(headers));
            setFooter(selectCurrentFixedSection(footers));
            setMiddleComponents(sections.sort(sortByPosition));
        } catch (error) {
            Swal.fire(error.message || 'Failed to load home screen layout', '', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchPreviewSections = async () => {
        try {
            setPreviewLoading(true);
            const response = await SectionServices.homeScreenPreview();
            if (response.success == '1') {
                setPreviewSections(response.data || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        fetchLayout();
        fetchPreviewSections();
    }, []);

    const orderedPreviewSections = useMemo(() => {
        const previewById = new Map(previewSections.map(section => [section.id, section]));
        const headerPreview = header ? previewById.get(header.id) || header : null;
        const footerPreview = footer ? previewById.get(footer.id) || footer : null;
        const usedIds = new Set([header?.id, footer?.id].filter(Boolean));

        const orderedMiddle = middleComponents.map(section => {
            usedIds.add(section.id);
            return previewById.get(section.id) || section;
        });

        const remainingMiddle = previewSections
            .filter(section => {
                if (usedIds.has(section.id)) return false;
                if (!section.isActive) return false;
                const pageType = getPageType(section);
                return pageType !== 'header' && pageType !== 'footer';
            })
            .sort(sortByPosition);

        return [headerPreview, ...orderedMiddle, ...remainingMiddle, footerPreview]
            .filter(Boolean)
            .filter(section => section.isActive !== false);
    }, [footer, header, middleComponents, previewSections]);

    const handleStatusChange = async (section) => {
        if (!section?.id) return;

        const result = await Swal.fire({
            text: 'Do you want to change component status now?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Change now!',
            cancelButtonText: 'No, Cancel',
            allowOutsideClick: false,
            allowEscapeKey: false,
        });

        if (!result.isConfirmed) return;

        try {
            setLoading(true);
            const response = await SectionServices.toggleStatus(section.id);
            if (response.success == '1') {
                await fetchLayout();
                await fetchPreviewSections();
                Swal.fire({ icon: 'success', title: 'Success', text: response.message || 'Status updated successfully', timer: 1500 });
            } else {
                Swal.fire({ icon: 'error', title: 'Failed', text: response.message || 'Failed to update status' });
            }
        } catch (error) {
            Swal.fire(error.message || 'Failed to update status', '', 'error');
        } finally {
            setLoading(false);
        }
    };

    const renderFixedComponent = (section, kind, fixedLabel) => {
        const meta = componentMeta[kind];
        return (
            <Card variant="outlined" sx={{ p: 1.5, borderRadius: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: 1, bgcolor: meta.bg, display: 'grid', placeItems: 'center' }}>
                        {meta.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>{meta.title}</Typography>
                            <Chip size="small" label={fixedLabel} sx={{ bgcolor: '#f3e8ff', color: '#6d28d9', fontSize: '0.65rem', height: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color: '#5f6b7a' }}>
                            {section?.description || meta.subtitle}
                        </Typography>
                    </Box>
                    <Switch size="small" checked={!!section?.isActive} onChange={() => handleStatusChange(section)} disabled={!section?.id} />
                    <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => navigate(kind === 'header' ? '/customer-app/header-management' : '/customer-app/footer-management')}>
                        Manage
                    </Button>
                    <IconButton size="small" onClick={() => navigate(kind === 'header' ? '/customer-app/header-management' : '/customer-app/footer-management')}>
                        <ArrowForwardIos sx={{ fontSize: '0.8rem' }} />
                    </IconButton>
                </Box>
            </Card>
        );
    };

    return (
        <>
            {loading && <Loading />}
            <PageHeaderLayout headerTitle="Manage Home Screen Layout">
                <Button sx={{ textTransform: 'none' }} size="small" variant="outlined" startIcon={<Preview />}>
                    Preview in App
                </Button>
            </PageHeaderLayout>

            <Grid container spacing={2}>
                <Grid item xs={12} lg={9}>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Layout Components</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#667085' }}>
                                    Manage the components that appear on the customer app home screen.
                                </Typography>
                            </Box>
                        </Box>

                        {renderFixedComponent(header, 'header', 'Fixed (Top)')}

                        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
                            {middleComponents.map((section, index) => {
                                const kind = getComponentKind(section);
                                const meta = componentMeta[kind] || componentMeta.section;
                                return (
                                    <Box
                                        key={section.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 1.5,
                                            py: 1.25,
                                            borderBottom: index === middleComponents.length - 1 ? 'none' : '1px solid #e5e7eb',
                                            bgcolor: section.isActive ? '#fff' : '#f9fafb',
                                            opacity: section.isActive ? 1 : 0.72
                                        }}
                                    >
                                        <Typography sx={{ width: 24, fontSize: '0.8rem', color: '#475467' }}>{index + 1}</Typography>
                                        <Box sx={{ width: 42, height: 42, borderRadius: 1, bgcolor: meta.bg, display: 'grid', placeItems: 'center' }}>
                                            {meta.icon}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>{getDisplayTitle(section)}</Typography>
                                                <Chip
                                                    size="small"
                                                    label={section.isActive ? 'Active' : 'Inactive'}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.65rem',
                                                        bgcolor: section.isActive ? '#dcfce7' : '#f3f4f6',
                                                        color: section.isActive ? '#166534' : '#6b7280'
                                                    }}
                                                />
                                            </Box>
                                            <Typography sx={{ fontSize: '0.72rem', color: '#5f6b7a' }}>
                                                {section.subtitle || section.description || meta.subtitle}
                                            </Typography>
                                        </Box>
                                        <Switch size="small" checked={!!section.isActive} onChange={() => handleStatusChange(section)} />
                                        <PermissionGuard feature='customer-app' permission='canEdit'>
                                            <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() => navigate(`/customer-app/home-screen/section/${section.id}?pageType=section`)}
                                            >
                                                <Edit sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </PermissionGuard>
                                    </Box>
                                );
                            })}
                        </Box>

                        {renderFixedComponent(footer, 'footer', 'Fixed (Bottom)')}
                    </Card>

                </Grid>

                <Grid item xs={12} lg={3}>
                    <Card variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>Live Preview</Typography>
                            <IconButton size="small" onClick={fetchPreviewSections}>
                                <Replay sx={{ fontSize: '0.95rem', color: '#667085' }} />
                            </IconButton>
                        </Box>
                        <PhoneFrame sections={orderedPreviewSections} loading={previewLoading || loading} width={248} screenHeight={455} />
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default HomeScreen;
